/**
 * Spelfases, in de volgorde waarin een seizoen ze doorloopt.
 *
 * Stond als losse string-array op ruim tien plekken in de code. Nu de fase op het
 * seizoen ingesteld kan worden en per veiling afgeweken mag worden, is één lijst
 * nodig die beide schermen delen.
 */
export const SPELFASES = [
    'Voorbereiding',
    'Veiling',
    'Blinde Pool',
    'Poulefase',
    'Tussenronde',
    'Draft',
    'Knock-out',
    'Finaleronde',
];

/**
 * De effectieve spelfase van een veiling.
 *
 * Het seizoen zet de standaard; een veiling mag daarvan afwijken door zelf een
 * fase te zetten. Staat er niets op de veiling, dan geldt die van het seizoen.
 *
 * @param {object} auction Het veilingdocument.
 * @param {string} [seasonSpelfase] De standaard van het seizoen.
 * @return {string} De fase die geldt, of een lege string als geen van beide gezet is.
 */
export function effectiveSpelfase(auction, seasonSpelfase) {
    return auction?.spelfase || seasonSpelfase || '';
}

/**
 * Maximum aantal spelers dat een deelnemer mag scouten.
 *
 * Gebruikt voor de veiling-competitie: wie de meeste van zijn (maximaal 5)
 * gescoute spelers binnenhaalt, wint. De lijst wordt in de Voorbereiding
 * samengesteld; tijdens de Veiling ligt hij vast (zie {@link isScoutingLocked}).
 */
export const MAX_SCOUTED_PLAYERS = 5;

/**
 * Of de scoutlijst in deze fase op slot zit: geen spelers toevoegen én geen
 * spelers verwijderen. Tijdens de Veiling ligt de lijst vast — die is in de
 * Voorbereiding samengesteld en mag tijdens de veiling niet meer gewijzigd
 * worden, zodat niemand zijn scouts nog aanpast terwijl er geveild wordt.
 *
 * @param {string} spelfase De effectieve spelfase.
 * @return {boolean}
 */
export function isScoutingLocked(spelfase) {
    return spelfase === 'Veiling';
}

/**
 * Het maximum aantal te scouten spelers in deze fase, of `null` als er geen
 * limiet geldt. In de Voorbereiding stelt elke deelnemer een lijst van maximaal
 * {@link MAX_SCOUTED_PLAYERS} samen; in andere fases geldt geen limiet.
 *
 * @param {string} spelfase De effectieve spelfase.
 * @return {number|null}
 */
export function scoutingLimit(spelfase) {
    return spelfase === 'Voorbereiding' ? MAX_SCOUTED_PLAYERS : null;
}

/**
 * De spelfases waarin de veiling-competitie ranglijst de normale ranglijst
 * vervangt: de Voorbereiding (scouten loopt, veilingavond moet nog komen) en de
 * Veiling zelf. Eén bron zodat de deelnemerspagina's dezelfde grens delen.
 */
export const AUCTION_COMPETITION_PHASES = ['Voorbereiding', 'Veiling'];

/**
 * Vertaling van de Engelse positienamen uit Sportmonks naar de Nederlandse
 * termen die de app (en `theme.positions`) gebruikt.
 *
 * Stond als losse const op ruim acht plekken in de code. Eén lijst zodat de
 * gedeelde `PlayerCard` (en op termijn de rest) er één bron voor heeft.
 */
export const POSITION_MAP = {
    Goalkeeper: 'Keeper',
    Defender: 'Verdediger',
    Midfielder: 'Middenvelder',
    Attacker: 'Aanvaller',
    Coach: 'Coach',
};

/**
 * Sentinel-club voor een speler die naar een club buiten dit seizoen (Non-CL) is
 * vertrokken. De import (functions/index.js) overschrijft dan de clubvelden van
 * de speler met dit id, `clubName: 'Non-CL'` en een lege `clubLogo`.
 */
export const NON_CL_CLUB_ID = 9999999;

/**
 * Of een speler naar een niet-CL-club is vertrokken en dus niet meer meespeelt.
 * Zulke spelers horen niet in keuze-/veilinglijsten thuis.
 *
 * @param {object} player Het spelerdocument.
 * @return {boolean}
 */
export function isNonClPlayer(player) {
    return player?.clubId === NON_CL_CLUB_ID || player?.clubName === 'Non-CL';
}

/**
 * Of een beheerder de speler in de stamgegevens op niet speelgerechtigd heeft
 * gezet. Zo'n speler hoort niet op de transfermarkt of in keuzelijsten.
 *
 * @param {object} player Het spelerdocument.
 * @return {boolean}
 */
export function isNotEligiblePlayer(player) {
    return player?.notEligible === true;
}

/** Transfertype waarmee een niet-speelgerechtigde speler uit een team gaat. */
export const NOT_ELIGIBLE_TRANSFER_TYPE = 'niet-speelgerechtigd';

/**
 * Transfertypes waarbij een speler buiten de deelnemer om uit zijn team gaat.
 * De deelnemer krijgt daarvoor een transfer terug om zijn team weer compleet te
 * maken. Moet gelijk blijven met REFUNDED_TRANSFER_TYPES in functions/index.js.
 */
export const REFUNDED_TRANSFER_TYPES = ['to-non-cl-club', NOT_ELIGIBLE_TRANSFER_TYPE];

/**
 * Of een speler nog meedoet in dit seizoen: zijn club moet een deelnemende
 * seizoensclub zijn. Dit vangt méér dan {@link isNonClPlayer}: ook spelers die van
 * een CL-club naar een andere niet-deelnemende club zijn vertrokken en daarbij het
 * Non-CL-sentinel misliepen (hun clubvelden wijzen dan naar de echte, niet-
 * deelnemende club i.p.v. naar 9999999/'Non-CL').
 *
 * `seasonClubIds` mag leeg zijn (nog niet geladen); dan valt de check terug op
 * alleen het Non-CL-sentinel, zodat er nooit per ongeluk álles verborgen wordt.
 *
 * @param {object} player Het spelerdocument.
 * @param {Set<number>} [seasonClubIds] Set van deelnemende club-ids (als Number).
 * @return {boolean}
 */
export function isActiveSeasonPlayer(player, seasonClubIds) {
    if (isNonClPlayer(player)) return false;
    if (isNotEligiblePlayer(player)) return false;
    if (!seasonClubIds || seasonClubIds.size === 0) return true;
    return seasonClubIds.has(Number(player?.clubId));
}
